/**
 * Generated by orval v8.22.0 🍺
 * Do not edit manually.
 * AutoGPT Agent Server
 * This server is used to execute agents that are created by the AutoGPT system.
 * OpenAPI spec version: 0.1
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type { BodyUploadWorkspaceFile } from "../../models/bodyUploadWorkspaceFile.ts";

import type { BulkMoveFilesRequest } from "../../models/bulkMoveFilesRequest.ts";

import type { DeleteFileResponse } from "../../models/deleteFileResponse.ts";

import type { GetWorkspaceFilePreviewParams } from "../../models/getWorkspaceFilePreviewParams.ts";

import type { HTTP401NotAuthenticatedErrorResponse } from "../../models/hTTP401NotAuthenticatedErrorResponse.ts";

import type { HTTPValidationError } from "../../models/hTTPValidationError.ts";

import type { ListFilesResponse } from "../../models/listFilesResponse.ts";

import type { ListWorkspaceFilesParams } from "../../models/listWorkspaceFilesParams.ts";

import type { RenameFileRequest } from "../../models/renameFileRequest.ts";

import type { StorageUsageResponse } from "../../models/storageUsageResponse.ts";

import type { UploadWorkspaceFileParams } from "../../models/uploadWorkspaceFileParams.ts";

import type { WorkspaceFile } from "../../models/workspaceFile.ts";

import type { WorkspaceFileItem } from "../../models/workspaceFileItem.ts";

import type { WorkspaceFileUploadResponse } from "../../models/workspaceFileUploadResponse.ts";

import type { WorkspaceFolder } from "../../models/workspaceFolder.ts";

import type { WorkspaceFolderCreateRequest } from "../../models/workspaceFolderCreateRequest.ts";

import type { WorkspaceFolderListResponse } from "../../models/workspaceFolderListResponse.ts";

import type { WorkspaceFolderUpdateRequest } from "../../models/workspaceFolderUpdateRequest.ts";

import { customMutator } from "../../../mutators/custom-mutator.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type listWorkspaceFilesResponse200 = {
  data: ListFilesResponse;
  status: 200;
};

export type listWorkspaceFilesResponse400 = {
  data: void;
  status: 400;
};

export type listWorkspaceFilesResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type listWorkspaceFilesResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type listWorkspaceFilesResponseSuccess =
  listWorkspaceFilesResponse200 & {
    headers: Headers;
  };
export type listWorkspaceFilesResponseError = (
  | listWorkspaceFilesResponse400
  | listWorkspaceFilesResponse401
  | listWorkspaceFilesResponse422
) & {
  headers: Headers;
};

export type listWorkspaceFilesResponse =
  | listWorkspaceFilesResponseSuccess
  | listWorkspaceFilesResponseError;

export const getListWorkspaceFilesUrl = (params?: ListWorkspaceFilesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/workspace/files?${stringifiedParams}`
    : `/api/workspace/files`;
};

/**
 * List files in the user's workspace.
 *
 * When session_id is provided, only files for that session are returned.
 * Otherwise, all files across sessions are listed. Results are paginated
 * via `limit`/`offset`; `has_more` indicates whether additional pages exist.
 *
 * The Artifacts page uses ``q`` for name search and ``origin`` to filter
 * between Uploaded (user-uploaded) and Generated (agent/block output) files.
 *
 * ``session_id`` (a per-session view) and the folder filters (``folder_id`` /
 * ``root_only``) are distinct, mutually exclusive axes, and ``folder_id`` and
 * ``root_only`` likewise conflict; passing conflicting filters returns a 400
 * rather than silently yielding an empty list.
 *
 * ``expert_id`` narrows the listing to that hired expert's own
 * conversations. It conflicts with ``session_id`` — both name which
 * conversations to show — but composes with the folder filters, which select
 * across the whole workspace. An expert the caller does not own (or no
 * longer has) yields an empty list.
 *
 * ``include_user_files`` widens an ``expert_id`` listing with the user's own
 * files, which an expert may read but which are nobody's conversation. It is
 * opt-in so that a view already filtered to one expert keeps showing that
 * expert's files and nothing else; the composer's picker sends it when its
 * "only this expert" filter is switched off.
 *
 * The listing is of the user's files: skill packages and Otto's working
 * files (``isInternal``, decided when each file is written) are left out in
 * the query, so paging stays correct. ``include_internal`` lists them too,
 * for a view that shows them apart.
 * @summary List workspace files
 */
export const listWorkspaceFiles = async (
  params?: ListWorkspaceFilesParams,
  options?: RequestInit,
): Promise<listWorkspaceFilesResponse> => {
  return customMutator<listWorkspaceFilesResponse>(
    getListWorkspaceFilesUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListWorkspaceFilesQueryKey = (
  params?: ListWorkspaceFilesParams,
) => {
  return [`/api/workspace/files`, ...(params ? [params] : [])] as const;
};

export const getListWorkspaceFilesQueryOptions = <
  TData = Awaited<ReturnType<typeof listWorkspaceFiles>>,
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  params?: ListWorkspaceFilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFiles>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListWorkspaceFilesQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listWorkspaceFiles>>
  > = ({ signal }) => listWorkspaceFiles(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listWorkspaceFiles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWorkspaceFilesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listWorkspaceFiles>>
>;
export type ListWorkspaceFilesQueryError =
  | void
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

export function useListWorkspaceFiles<
  TData = Awaited<ReturnType<typeof listWorkspaceFiles>>,
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  params: undefined | ListWorkspaceFilesParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFiles>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFiles>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFiles>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListWorkspaceFiles<
  TData = Awaited<ReturnType<typeof listWorkspaceFiles>>,
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  params?: ListWorkspaceFilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFiles>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFiles>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFiles>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListWorkspaceFiles<
  TData = Awaited<ReturnType<typeof listWorkspaceFiles>>,
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  params?: ListWorkspaceFilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFiles>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List workspace files
 */

export function useListWorkspaceFiles<
  TData = Awaited<ReturnType<typeof listWorkspaceFiles>>,
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  params?: ListWorkspaceFilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFiles>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListWorkspaceFilesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List workspace files
 */
export const prefetchListWorkspaceFilesQuery = async <
  TData = Awaited<ReturnType<typeof listWorkspaceFiles>>,
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  queryClient: QueryClient,
  params?: ListWorkspaceFilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFiles>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListWorkspaceFilesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type uploadWorkspaceFileResponse200 = {
  data: WorkspaceFileUploadResponse;
  status: 200;
};

export type uploadWorkspaceFileResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type uploadWorkspaceFileResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type uploadWorkspaceFileResponseSuccess =
  uploadWorkspaceFileResponse200 & {
    headers: Headers;
  };
export type uploadWorkspaceFileResponseError = (
  | uploadWorkspaceFileResponse401
  | uploadWorkspaceFileResponse422
) & {
  headers: Headers;
};

export type uploadWorkspaceFileResponse =
  | uploadWorkspaceFileResponseSuccess
  | uploadWorkspaceFileResponseError;

export const getUploadWorkspaceFileUrl = (
  params?: UploadWorkspaceFileParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/workspace/files/upload?${stringifiedParams}`
    : `/api/workspace/files/upload`;
};

/**
 * Upload a file to the user's workspace.
 *
 * Files are stored in session-scoped paths when session_id is provided,
 * so the agent's session-scoped tools can discover them automatically.
 * @summary Upload file to workspace
 */
export const uploadWorkspaceFile = async (
  bodyUploadWorkspaceFile: BodyUploadWorkspaceFile,
  params?: UploadWorkspaceFileParams,
  options?: RequestInit,
): Promise<uploadWorkspaceFileResponse> => {
  const formData = new FormData();
  formData.append(`file`, bodyUploadWorkspaceFile.file);

  return customMutator<uploadWorkspaceFileResponse>(
    getUploadWorkspaceFileUrl(params),
    {
      ...options,
      method: "POST",
      body: formData,
    },
  );
};

export const getUploadWorkspaceFileMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadWorkspaceFile>>,
    TError,
    { data: BodyUploadWorkspaceFile; params?: UploadWorkspaceFileParams },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadWorkspaceFile>>,
  TError,
  { data: BodyUploadWorkspaceFile; params?: UploadWorkspaceFileParams },
  TContext
> => {
  const mutationKey = ["uploadWorkspaceFile"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadWorkspaceFile>>,
    { data: BodyUploadWorkspaceFile; params?: UploadWorkspaceFileParams }
  > = (props) => {
    const { data, params } = props ?? {};

    return uploadWorkspaceFile(data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadWorkspaceFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadWorkspaceFile>>
>;
export type UploadWorkspaceFileMutationBody = BodyUploadWorkspaceFile;
export type UploadWorkspaceFileMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Upload file to workspace
 */
export const useUploadWorkspaceFile = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadWorkspaceFile>>,
      TError,
      { data: BodyUploadWorkspaceFile; params?: UploadWorkspaceFileParams },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadWorkspaceFile>>,
  TError,
  { data: BodyUploadWorkspaceFile; params?: UploadWorkspaceFileParams },
  TContext
> => {
  return useMutation(
    getUploadWorkspaceFileMutationOptions(options),
    queryClient,
  );
};
export type deleteWorkspaceFileResponse200 = {
  data: DeleteFileResponse;
  status: 200;
};

export type deleteWorkspaceFileResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type deleteWorkspaceFileResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type deleteWorkspaceFileResponseSuccess =
  deleteWorkspaceFileResponse200 & {
    headers: Headers;
  };
export type deleteWorkspaceFileResponseError = (
  | deleteWorkspaceFileResponse401
  | deleteWorkspaceFileResponse422
) & {
  headers: Headers;
};

export type deleteWorkspaceFileResponse =
  | deleteWorkspaceFileResponseSuccess
  | deleteWorkspaceFileResponseError;

export const getDeleteWorkspaceFileUrl = (fileId: string) => {
  return `/api/workspace/files/${fileId}`;
};

/**
 * Soft-delete a workspace file and attempt to remove it from storage.
 *
 * Used when a user clears a file input in the builder.
 * @summary Delete a workspace file
 */
export const deleteWorkspaceFile = async (
  fileId: string,
  options?: RequestInit,
): Promise<deleteWorkspaceFileResponse> => {
  return customMutator<deleteWorkspaceFileResponse>(
    getDeleteWorkspaceFileUrl(fileId),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDeleteWorkspaceFileMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWorkspaceFile>>,
    TError,
    { fileId: string },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteWorkspaceFile>>,
  TError,
  { fileId: string },
  TContext
> => {
  const mutationKey = ["deleteWorkspaceFile"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteWorkspaceFile>>,
    { fileId: string }
  > = (props) => {
    const { fileId } = props ?? {};

    return deleteWorkspaceFile(fileId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteWorkspaceFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteWorkspaceFile>>
>;

export type DeleteWorkspaceFileMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Delete a workspace file
 */
export const useDeleteWorkspaceFile = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteWorkspaceFile>>,
      TError,
      { fileId: string },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteWorkspaceFile>>,
  TError,
  { fileId: string },
  TContext
> => {
  return useMutation(
    getDeleteWorkspaceFileMutationOptions(options),
    queryClient,
  );
};
export type renameWorkspaceFileResponse200 = {
  data: WorkspaceFileItem;
  status: 200;
};

export type renameWorkspaceFileResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type renameWorkspaceFileResponse404 = {
  data: void;
  status: 404;
};

export type renameWorkspaceFileResponse409 = {
  data: void;
  status: 409;
};

export type renameWorkspaceFileResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type renameWorkspaceFileResponseSuccess =
  renameWorkspaceFileResponse200 & {
    headers: Headers;
  };
export type renameWorkspaceFileResponseError = (
  | renameWorkspaceFileResponse401
  | renameWorkspaceFileResponse404
  | renameWorkspaceFileResponse409
  | renameWorkspaceFileResponse422
) & {
  headers: Headers;
};

export type renameWorkspaceFileResponse =
  | renameWorkspaceFileResponseSuccess
  | renameWorkspaceFileResponseError;

export const getRenameWorkspaceFileUrl = (fileId: string) => {
  return `/api/workspace/files/${fileId}`;
};

/**
 * Rename a file; it stays in its folder and conversation.
 * @summary Rename a workspace file
 */
export const renameWorkspaceFile = async (
  fileId: string,
  renameFileRequest: RenameFileRequest,
  options?: RequestInit,
): Promise<renameWorkspaceFileResponse> => {
  return customMutator<renameWorkspaceFileResponse>(
    getRenameWorkspaceFileUrl(fileId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(renameFileRequest),
    },
  );
};

export const getRenameWorkspaceFileMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameWorkspaceFile>>,
    TError,
    { fileId: string; data: RenameFileRequest },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof renameWorkspaceFile>>,
  TError,
  { fileId: string; data: RenameFileRequest },
  TContext
> => {
  const mutationKey = ["renameWorkspaceFile"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof renameWorkspaceFile>>,
    { fileId: string; data: RenameFileRequest }
  > = (props) => {
    const { fileId, data } = props ?? {};

    return renameWorkspaceFile(fileId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RenameWorkspaceFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof renameWorkspaceFile>>
>;
export type RenameWorkspaceFileMutationBody = RenameFileRequest;
export type RenameWorkspaceFileMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | void
  | HTTPValidationError;

/**
 * @summary Rename a workspace file
 */
export const useRenameWorkspaceFile = <
  TError = HTTP401NotAuthenticatedErrorResponse | void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof renameWorkspaceFile>>,
      TError,
      { fileId: string; data: RenameFileRequest },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof renameWorkspaceFile>>,
  TError,
  { fileId: string; data: RenameFileRequest },
  TContext
> => {
  return useMutation(
    getRenameWorkspaceFileMutationOptions(options),
    queryClient,
  );
};
export type getWorkspaceDownloadFileByIdResponse200 = {
  data: unknown;
  status: 200;
};

export type getWorkspaceDownloadFileByIdResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getWorkspaceDownloadFileByIdResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getWorkspaceDownloadFileByIdResponseSuccess =
  getWorkspaceDownloadFileByIdResponse200 & {
    headers: Headers;
  };
export type getWorkspaceDownloadFileByIdResponseError = (
  | getWorkspaceDownloadFileByIdResponse401
  | getWorkspaceDownloadFileByIdResponse422
) & {
  headers: Headers;
};

export type getWorkspaceDownloadFileByIdResponse =
  | getWorkspaceDownloadFileByIdResponseSuccess
  | getWorkspaceDownloadFileByIdResponseError;

export const getGetWorkspaceDownloadFileByIdUrl = (fileId: string) => {
  return `/api/workspace/files/${fileId}/download`;
};

/**
 * Download a file by its ID.
 *
 * Returns the file content directly or redirects to a signed URL for GCS.
 * @summary Download file by ID
 */
export const getWorkspaceDownloadFileById = async (
  fileId: string,
  options?: RequestInit,
): Promise<getWorkspaceDownloadFileByIdResponse> => {
  return customMutator<getWorkspaceDownloadFileByIdResponse>(
    getGetWorkspaceDownloadFileByIdUrl(fileId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetWorkspaceDownloadFileByIdQueryKey = (fileId: string) => {
  return [`/api/workspace/files/${fileId}/download`] as const;
};

export const getGetWorkspaceDownloadFileByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetWorkspaceDownloadFileByIdQueryKey(fileId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>
  > = ({ signal }) =>
    getWorkspaceDownloadFileById(fileId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: fileId !== null && fileId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWorkspaceDownloadFileByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>
>;
export type GetWorkspaceDownloadFileByIdQueryError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

export function useGetWorkspaceDownloadFileById<
  TData = Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetWorkspaceDownloadFileById<
  TData = Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetWorkspaceDownloadFileById<
  TData = Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Download file by ID
 */

export function useGetWorkspaceDownloadFileById<
  TData = Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetWorkspaceDownloadFileByIdQueryOptions(
    fileId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Download file by ID
 */
export const prefetchGetWorkspaceDownloadFileByIdQuery = async <
  TData = Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  queryClient: QueryClient,
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceDownloadFileById>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkspaceDownloadFileByIdQueryOptions(
    fileId,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type getWorkspaceFilePreviewResponse200 = {
  data: unknown;
  status: 200;
};

export type getWorkspaceFilePreviewResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getWorkspaceFilePreviewResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type getWorkspaceFilePreviewResponseSuccess =
  getWorkspaceFilePreviewResponse200 & {
    headers: Headers;
  };
export type getWorkspaceFilePreviewResponseError = (
  | getWorkspaceFilePreviewResponse401
  | getWorkspaceFilePreviewResponse422
) & {
  headers: Headers;
};

export type getWorkspaceFilePreviewResponse =
  | getWorkspaceFilePreviewResponseSuccess
  | getWorkspaceFilePreviewResponseError;

export const getGetWorkspaceFilePreviewUrl = (
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/workspace/files/${fileId}/preview?${stringifiedParams}`
    : `/api/workspace/files/${fileId}/preview`;
};

/**
 * Return a cheap preview of a file.
 *
 * Images/PDFs/Office docs are returned as a small WebP thumbnail; text-like
 * files return only their first ``bytes`` bytes. Used by the Artifacts page so
 * a grid of files no longer downloads every file in full.
 * @summary Preview file by ID
 */
export const getWorkspaceFilePreview = async (
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
  options?: RequestInit,
): Promise<getWorkspaceFilePreviewResponse> => {
  return customMutator<getWorkspaceFilePreviewResponse>(
    getGetWorkspaceFilePreviewUrl(fileId, params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetWorkspaceFilePreviewQueryKey = (
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
) => {
  return [
    `/api/workspace/files/${fileId}/preview`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetWorkspaceFilePreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkspaceFilePreviewQueryKey(fileId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkspaceFilePreview>>
  > = ({ signal }) =>
    getWorkspaceFilePreview(fileId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: fileId !== null && fileId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWorkspaceFilePreviewQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWorkspaceFilePreview>>
>;
export type GetWorkspaceFilePreviewQueryError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

export function useGetWorkspaceFilePreview<
  TData = Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  params: undefined | GetWorkspaceFilePreviewParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceFilePreview>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetWorkspaceFilePreview<
  TData = Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceFilePreview>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetWorkspaceFilePreview<
  TData = Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Preview file by ID
 */

export function useGetWorkspaceFilePreview<
  TData = Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetWorkspaceFilePreviewQueryOptions(
    fileId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Preview file by ID
 */
export const prefetchGetWorkspaceFilePreviewQuery = async <
  TData = Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
>(
  queryClient: QueryClient,
  fileId: string,
  params?: GetWorkspaceFilePreviewParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceFilePreview>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkspaceFilePreviewQueryOptions(
    fileId,
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type listWorkspaceFoldersResponse200 = {
  data: WorkspaceFolderListResponse;
  status: 200;
};

export type listWorkspaceFoldersResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type listWorkspaceFoldersResponseSuccess =
  listWorkspaceFoldersResponse200 & {
    headers: Headers;
  };
export type listWorkspaceFoldersResponseError =
  listWorkspaceFoldersResponse401 & {
    headers: Headers;
  };

export type listWorkspaceFoldersResponse =
  | listWorkspaceFoldersResponseSuccess
  | listWorkspaceFoldersResponseError;

export const getListWorkspaceFoldersUrl = () => {
  return `/api/workspace/folders`;
};

/**
 * Every folder in the workspace, flat; nesting is read off ``parent_id``.
 *
 * ``file_count`` counts the files directly in each folder, not its subtree.
 * @summary List workspace folders
 */
export const listWorkspaceFolders = async (
  options?: RequestInit,
): Promise<listWorkspaceFoldersResponse> => {
  return customMutator<listWorkspaceFoldersResponse>(
    getListWorkspaceFoldersUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getListWorkspaceFoldersQueryKey = () => {
  return [`/api/workspace/folders`] as const;
};

export const getListWorkspaceFoldersQueryOptions = <
  TData = Awaited<ReturnType<typeof listWorkspaceFolders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof listWorkspaceFolders>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWorkspaceFoldersQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listWorkspaceFolders>>
  > = ({ signal }) => listWorkspaceFolders({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listWorkspaceFolders>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWorkspaceFoldersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listWorkspaceFolders>>
>;
export type ListWorkspaceFoldersQueryError =
  HTTP401NotAuthenticatedErrorResponse;

export function useListWorkspaceFolders<
  TData = Awaited<ReturnType<typeof listWorkspaceFolders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFolders>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFolders>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFolders>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListWorkspaceFolders<
  TData = Awaited<ReturnType<typeof listWorkspaceFolders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFolders>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaceFolders>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaceFolders>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListWorkspaceFolders<
  TData = Awaited<ReturnType<typeof listWorkspaceFolders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFolders>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List workspace folders
 */

export function useListWorkspaceFolders<
  TData = Awaited<ReturnType<typeof listWorkspaceFolders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFolders>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListWorkspaceFoldersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List workspace folders
 */
export const prefetchListWorkspaceFoldersQuery = async <
  TData = Awaited<ReturnType<typeof listWorkspaceFolders>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listWorkspaceFolders>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListWorkspaceFoldersQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

export type createWorkspaceFolderResponse201 = {
  data: WorkspaceFolder;
  status: 201;
};

export type createWorkspaceFolderResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type createWorkspaceFolderResponse404 = {
  data: void;
  status: 404;
};

export type createWorkspaceFolderResponse409 = {
  data: void;
  status: 409;
};

export type createWorkspaceFolderResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type createWorkspaceFolderResponseSuccess =
  createWorkspaceFolderResponse201 & {
    headers: Headers;
  };
export type createWorkspaceFolderResponseError = (
  | createWorkspaceFolderResponse401
  | createWorkspaceFolderResponse404
  | createWorkspaceFolderResponse409
  | createWorkspaceFolderResponse422
) & {
  headers: Headers;
};

export type createWorkspaceFolderResponse =
  | createWorkspaceFolderResponseSuccess
  | createWorkspaceFolderResponseError;

export const getCreateWorkspaceFolderUrl = () => {
  return `/api/workspace/folders`;
};

/**
 * @summary Create workspace folder
 */
export const createWorkspaceFolder = async (
  workspaceFolderCreateRequest: WorkspaceFolderCreateRequest,
  options?: RequestInit,
): Promise<createWorkspaceFolderResponse> => {
  return customMutator<createWorkspaceFolderResponse>(
    getCreateWorkspaceFolderUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(workspaceFolderCreateRequest),
    },
  );
};

export const getCreateWorkspaceFolderMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createWorkspaceFolder>>,
    TError,
    { data: WorkspaceFolderCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createWorkspaceFolder>>,
  TError,
  { data: WorkspaceFolderCreateRequest },
  TContext
> => {
  const mutationKey = ["createWorkspaceFolder"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createWorkspaceFolder>>,
    { data: WorkspaceFolderCreateRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createWorkspaceFolder(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateWorkspaceFolderMutationResult = NonNullable<
  Awaited<ReturnType<typeof createWorkspaceFolder>>
>;
export type CreateWorkspaceFolderMutationBody = WorkspaceFolderCreateRequest;
export type CreateWorkspaceFolderMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | void
  | HTTPValidationError;

/**
 * @summary Create workspace folder
 */
export const useCreateWorkspaceFolder = <
  TError = HTTP401NotAuthenticatedErrorResponse | void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createWorkspaceFolder>>,
      TError,
      { data: WorkspaceFolderCreateRequest },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createWorkspaceFolder>>,
  TError,
  { data: WorkspaceFolderCreateRequest },
  TContext
> => {
  return useMutation(
    getCreateWorkspaceFolderMutationOptions(options),
    queryClient,
  );
};
export type bulkMoveWorkspaceFilesResponse200 = {
  data: WorkspaceFile[];
  status: 200;
};

export type bulkMoveWorkspaceFilesResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type bulkMoveWorkspaceFilesResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type bulkMoveWorkspaceFilesResponseSuccess =
  bulkMoveWorkspaceFilesResponse200 & {
    headers: Headers;
  };
export type bulkMoveWorkspaceFilesResponseError = (
  | bulkMoveWorkspaceFilesResponse401
  | bulkMoveWorkspaceFilesResponse422
) & {
  headers: Headers;
};

export type bulkMoveWorkspaceFilesResponse =
  | bulkMoveWorkspaceFilesResponseSuccess
  | bulkMoveWorkspaceFilesResponseError;

export const getBulkMoveWorkspaceFilesUrl = () => {
  return `/api/workspace/folders/files/bulk-move`;
};

/**
 * @summary Move workspace files to a folder
 */
export const bulkMoveWorkspaceFiles = async (
  bulkMoveFilesRequest: BulkMoveFilesRequest,
  options?: RequestInit,
): Promise<bulkMoveWorkspaceFilesResponse> => {
  return customMutator<bulkMoveWorkspaceFilesResponse>(
    getBulkMoveWorkspaceFilesUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(bulkMoveFilesRequest),
    },
  );
};

export const getBulkMoveWorkspaceFilesMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof bulkMoveWorkspaceFiles>>,
    TError,
    { data: BulkMoveFilesRequest },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof bulkMoveWorkspaceFiles>>,
  TError,
  { data: BulkMoveFilesRequest },
  TContext
> => {
  const mutationKey = ["bulkMoveWorkspaceFiles"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof bulkMoveWorkspaceFiles>>,
    { data: BulkMoveFilesRequest }
  > = (props) => {
    const { data } = props ?? {};

    return bulkMoveWorkspaceFiles(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type BulkMoveWorkspaceFilesMutationResult = NonNullable<
  Awaited<ReturnType<typeof bulkMoveWorkspaceFiles>>
>;
export type BulkMoveWorkspaceFilesMutationBody = BulkMoveFilesRequest;
export type BulkMoveWorkspaceFilesMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Move workspace files to a folder
 */
export const useBulkMoveWorkspaceFiles = <
  TError = HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof bulkMoveWorkspaceFiles>>,
      TError,
      { data: BulkMoveFilesRequest },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof bulkMoveWorkspaceFiles>>,
  TError,
  { data: BulkMoveFilesRequest },
  TContext
> => {
  return useMutation(
    getBulkMoveWorkspaceFilesMutationOptions(options),
    queryClient,
  );
};
export type deleteWorkspaceFolderResponse204 = {
  data: void;
  status: 204;
};

export type deleteWorkspaceFolderResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type deleteWorkspaceFolderResponse404 = {
  data: void;
  status: 404;
};

export type deleteWorkspaceFolderResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type deleteWorkspaceFolderResponseSuccess =
  deleteWorkspaceFolderResponse204 & {
    headers: Headers;
  };
export type deleteWorkspaceFolderResponseError = (
  | deleteWorkspaceFolderResponse401
  | deleteWorkspaceFolderResponse404
  | deleteWorkspaceFolderResponse422
) & {
  headers: Headers;
};

export type deleteWorkspaceFolderResponse =
  | deleteWorkspaceFolderResponseSuccess
  | deleteWorkspaceFolderResponseError;

export const getDeleteWorkspaceFolderUrl = (folderId: string) => {
  return `/api/workspace/folders/${folderId}`;
};

/**
 * Delete a folder and its subfolders; their files return to the root.
 * @summary Delete workspace folder
 */
export const deleteWorkspaceFolder = async (
  folderId: string,
  options?: RequestInit,
): Promise<deleteWorkspaceFolderResponse> => {
  return customMutator<deleteWorkspaceFolderResponse>(
    getDeleteWorkspaceFolderUrl(folderId),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDeleteWorkspaceFolderMutationOptions = <
  TError = HTTP401NotAuthenticatedErrorResponse | void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWorkspaceFolder>>,
    TError,
    { folderId: string },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteWorkspaceFolder>>,
  TError,
  { folderId: string },
  TContext
> => {
  const mutationKey = ["deleteWorkspaceFolder"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteWorkspaceFolder>>,
    { folderId: string }
  > = (props) => {
    const { folderId } = props ?? {};

    return deleteWorkspaceFolder(folderId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteWorkspaceFolderMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteWorkspaceFolder>>
>;

export type DeleteWorkspaceFolderMutationError =
  | HTTP401NotAuthenticatedErrorResponse
  | void
  | HTTPValidationError;

/**
 * @summary Delete workspace folder
 */
export const useDeleteWorkspaceFolder = <
  TError = HTTP401NotAuthenticatedErrorResponse | void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteWorkspaceFolder>>,
      TError,
      { folderId: string },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteWorkspaceFolder>>,
  TError,
  { folderId: string },
  TContext
> => {
  return useMutation(
    getDeleteWorkspaceFolderMutationOptions(options),
    queryClient,
  );
};
export type updateWorkspaceFolderResponse200 = {
  data: WorkspaceFolder;
  status: 200;
};

export type updateWorkspaceFolderResponse400 = {
  data: void;
  status: 400;
};

export type updateWorkspaceFolderResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type updateWorkspaceFolderResponse404 = {
  data: void;
  status: 404;
};

export type updateWorkspaceFolderResponse409 = {
  data: void;
  status: 409;
};

export type updateWorkspaceFolderResponse422 = {
  data: HTTPValidationError;
  status: 422;
};

export type updateWorkspaceFolderResponseSuccess =
  updateWorkspaceFolderResponse200 & {
    headers: Headers;
  };
export type updateWorkspaceFolderResponseError = (
  | updateWorkspaceFolderResponse400
  | updateWorkspaceFolderResponse401
  | updateWorkspaceFolderResponse404
  | updateWorkspaceFolderResponse409
  | updateWorkspaceFolderResponse422
) & {
  headers: Headers;
};

export type updateWorkspaceFolderResponse =
  | updateWorkspaceFolderResponseSuccess
  | updateWorkspaceFolderResponseError;

export const getUpdateWorkspaceFolderUrl = (folderId: string) => {
  return `/api/workspace/folders/${folderId}`;
};

/**
 * Rename a folder, change its icon, and/or move it.
 *
 * Sending ``parent_id: null`` moves the folder to the workspace root;
 * leaving the field out keeps it where it is. Move and rename are applied
 * together or not at all, and the new name is checked for a clash against
 * the destination.
 * @summary Update workspace folder
 */
export const updateWorkspaceFolder = async (
  folderId: string,
  workspaceFolderUpdateRequest: WorkspaceFolderUpdateRequest,
  options?: RequestInit,
): Promise<updateWorkspaceFolderResponse> => {
  return customMutator<updateWorkspaceFolderResponse>(
    getUpdateWorkspaceFolderUrl(folderId),
    {
      ...options,
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(workspaceFolderUpdateRequest),
    },
  );
};

export const getUpdateWorkspaceFolderMutationOptions = <
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateWorkspaceFolder>>,
    TError,
    { folderId: string; data: WorkspaceFolderUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customMutator>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateWorkspaceFolder>>,
  TError,
  { folderId: string; data: WorkspaceFolderUpdateRequest },
  TContext
> => {
  const mutationKey = ["updateWorkspaceFolder"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateWorkspaceFolder>>,
    { folderId: string; data: WorkspaceFolderUpdateRequest }
  > = (props) => {
    const { folderId, data } = props ?? {};

    return updateWorkspaceFolder(folderId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateWorkspaceFolderMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateWorkspaceFolder>>
>;
export type UpdateWorkspaceFolderMutationBody = WorkspaceFolderUpdateRequest;
export type UpdateWorkspaceFolderMutationError =
  | void
  | HTTP401NotAuthenticatedErrorResponse
  | HTTPValidationError;

/**
 * @summary Update workspace folder
 */
export const useUpdateWorkspaceFolder = <
  TError = void | HTTP401NotAuthenticatedErrorResponse | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateWorkspaceFolder>>,
      TError,
      { folderId: string; data: WorkspaceFolderUpdateRequest },
      TContext
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateWorkspaceFolder>>,
  TError,
  { folderId: string; data: WorkspaceFolderUpdateRequest },
  TContext
> => {
  return useMutation(
    getUpdateWorkspaceFolderMutationOptions(options),
    queryClient,
  );
};
export type getWorkspaceStorageUsageResponse200 = {
  data: StorageUsageResponse;
  status: 200;
};

export type getWorkspaceStorageUsageResponse401 = {
  data: HTTP401NotAuthenticatedErrorResponse;
  status: 401;
};

export type getWorkspaceStorageUsageResponseSuccess =
  getWorkspaceStorageUsageResponse200 & {
    headers: Headers;
  };
export type getWorkspaceStorageUsageResponseError =
  getWorkspaceStorageUsageResponse401 & {
    headers: Headers;
  };

export type getWorkspaceStorageUsageResponse =
  | getWorkspaceStorageUsageResponseSuccess
  | getWorkspaceStorageUsageResponseError;

export const getGetWorkspaceStorageUsageUrl = () => {
  return `/api/workspace/storage/usage`;
};

/**
 * Get storage usage information for the user's workspace.
 * @summary Get workspace storage usage
 */
export const getWorkspaceStorageUsage = async (
  options?: RequestInit,
): Promise<getWorkspaceStorageUsageResponse> => {
  return customMutator<getWorkspaceStorageUsageResponse>(
    getGetWorkspaceStorageUsageUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetWorkspaceStorageUsageQueryKey = () => {
  return [`/api/workspace/storage/usage`] as const;
};

export const getGetWorkspaceStorageUsageQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customMutator>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetWorkspaceStorageUsageQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkspaceStorageUsage>>
  > = ({ signal }) => getWorkspaceStorageUsage({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWorkspaceStorageUsageQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWorkspaceStorageUsage>>
>;
export type GetWorkspaceStorageUsageQueryError =
  HTTP401NotAuthenticatedErrorResponse;

export function useGetWorkspaceStorageUsage<
  TData = Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceStorageUsage>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetWorkspaceStorageUsage<
  TData = Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
          TError,
          Awaited<ReturnType<typeof getWorkspaceStorageUsage>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetWorkspaceStorageUsage<
  TData = Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get workspace storage usage
 */

export function useGetWorkspaceStorageUsage<
  TData = Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetWorkspaceStorageUsageQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get workspace storage usage
 */
export const prefetchGetWorkspaceStorageUsageQuery = async <
  TData = Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
  TError = HTTP401NotAuthenticatedErrorResponse,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getWorkspaceStorageUsage>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customMutator>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkspaceStorageUsageQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};
